import axios from "axios";

const uiApi = axios.create();

uiApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("ui_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface UiUser {
  id: string;
  nome: string;
  email: string;
  "api-key"?: string;
  apiKey?: string;
  apiKeyAnonymized?: string;
  role: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UiLoginResponse {
  token: string;
  name: string;
  email: string;
  role: string;
  apiKeyAnonymized: string;
}

export interface UiApiKey {
  xApiKey: string;
  company?: string;
  responsibleEmail?: string;
  mostApiKey?: string;
  mainTenant?: string;
  ambiente?: string;
  allowedOrigins?: string[];
  apiKey?: string;
  apiKeyAnonymized?: string;
  tenant?: string;
}

export interface SignerInput {
  name: string;
  email: string;
  document: string;
  signatureType?: string;
}

export interface CreateSignatureInput {
  title: string;
  description?: string;
  authorName: string;
  authorEmail: string;
  authorDocument: string;
  tenant?: string;
  signers: SignerInput[];
  file: File;
  callback?: string;
  sendLinkAndOtp?: boolean;
  signatureDueDate?: string;
}

export const loginUi = async (email: string, senha: string): Promise<UiLoginResponse> => {
  const response = await uiApi.post<UiLoginResponse>("/api/v1/ui/auth/login", {
    email,
    password: senha,
  });
  return response.data;
};

export const forgotPasswordUi = async (email: string): Promise<void> => {
  await uiApi.post("/api/v1/ui/auth/forgot-password", { email });
};

export const resetPasswordUi = async (code: string, newPassword: string): Promise<void> => {
  await uiApi.post("/api/v1/ui/auth/reset-password", { code, newPassword });
};

export const getSignaturesUi = async (page = 0, size = 10, q?: string): Promise<unknown> => {
  const response = await uiApi.get("/api/v1/ui/signatures", {
    params: { page, size, q: q || undefined },
  });
  return response.data as unknown;
};

export const getSignatureUi = async (id: string): Promise<unknown> => {
  const response = await uiApi.get(`/api/v1/ui/signatures/${id}`);
  return response.data as unknown;
};

export const createSignatureUi = async (input: CreateSignatureInput): Promise<unknown> => {
  const formData = new FormData();
  const signatureInputPayload = {
    title: input.title,
    description: input.description,
    authorName: input.authorName,
    authorEmail: input.authorEmail,
    authorDocument: input.authorDocument,
    tenant: input.tenant,
    signers: input.signers,
    callback: input.callback,
    sendLinkAndOtp: input.sendLinkAndOtp ?? true,
    signatureDueDate: input.signatureDueDate || undefined,
  };

  formData.append(
    "signatureInput",
    new Blob([JSON.stringify(signatureInputPayload)], { type: "application/json" }),
  );
  formData.append("file", input.file);

  const response = await uiApi.post("/api/v1/ui/signatures", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data as unknown;
};

export const getUsersUi = async (page = 0, size = 10, q?: string): Promise<unknown> => {
  const response = await uiApi.get("/api/v1/ui/admin/users", {
    params: { page, size, q: q || undefined },
  });
  return response.data as unknown;
};

export const getUserUi = async (id: string): Promise<UiUser> => {
  const response = await uiApi.get<UiUser>(`/api/v1/ui/admin/users/${id}`);
  return response.data;
};

export const createUserUi = async (user: {
  nome: string;
  email: string;
  senha: string;
  "api-key": string;
}): Promise<UiUser> => {
  const response = await uiApi.post<UiUser>("/api/v1/ui/admin/users", user);
  return response.data;
};

export const updateUserUi = async (
  id: string,
  user: {
    nome: string;
    email: string;
    senha?: string;
    "api-key": string;
  },
): Promise<UiUser> => {
  const response = await uiApi.put<UiUser>(`/api/v1/ui/admin/users/${id}`, user);
  return response.data;
};

export const deleteUserUi = async (id: string): Promise<void> => {
  await uiApi.delete(`/api/v1/ui/admin/users/${id}`);
};

export const getApiKeysUi = async (): Promise<UiApiKey[]> => {
  const response = await uiApi.get<UiApiKey[]>("/api/v1/ui/admin/api-keys");
  return response.data;
};
